/**
 * First-touch campaign parameter persistence for the current browser tab.
 * Captures UTMs (and the gclid / msclkid ad click IDs) from the landing URL so
 * checkout / purchase events
 * still have attribution after the query string is gone.
 */

const STORAGE_KEY = 'gt_campaign_params';

const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_content',
  'utm_term',
  'gclid',
  'msclkid',
] as const;

export type CampaignParamKey = (typeof CAMPAIGN_KEYS)[number];

export type CampaignParams = Partial<Record<CampaignParamKey, string>>;

export type TrackingUtmParams = {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmId: string | null;
  gclid: string | null;
  msclkid: string | null;
};

const readStore = (): CampaignParams => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }
    return parsed as CampaignParams;
  } catch {
    return {};
  }
};

const writeStore = (params: CampaignParams): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch {
    // Private mode / quota — attribution falls back to live URL only.
  }
};

const paramsFromSearch = (search: string): CampaignParams => {
  const urlParams = new URLSearchParams(search);
  const params: CampaignParams = {};

  for (const key of CAMPAIGN_KEYS) {
    const value = urlParams.get(key);
    if (value) {
      params[key] = value;
    }
  }

  return params;
};

/** First-touch: keep existing stored values; fill only missing keys from the URL. */
export const captureCampaignParamsFromUrl = (
  search: string = window.location.search,
): CampaignParams => {
  const fromUrl = paramsFromSearch(search);
  if (Object.keys(fromUrl).length === 0) {
    return readStore();
  }

  const stored = readStore();
  const merged: CampaignParams = { ...fromUrl, ...stored };

  // First-touch wins: stored values override newly seen URL values.
  writeStore(merged);
  return merged;
};

export const getPersistedCampaignParams = (): CampaignParams => readStore();

/**
 * Resolves campaign params for tracking (first-touch store wins over later URLs).
 * Reports only what the visitor actually arrived with: every field is null when absent.
 * No brand fallback — inventing a source here overrode GA4's own referral / organic /
 * direct attribution. Plato keeps its own server-side fallback in Tracking.cs, which the
 * API validates and which is deliberately unaffected by this.
 */
export const resolveTrackingUtmParams = (): TrackingUtmParams => {
  const fromUrl = paramsFromSearch(window.location.search);
  const stored = readStore();
  const merged: CampaignParams = { ...fromUrl, ...stored };

  return {
    utmSource: merged.utm_source || null,
    utmMedium: merged.utm_medium || null,
    utmCampaign: merged.utm_campaign || null,
    utmId: merged.utm_id || null,
    gclid: merged.gclid || null,
    msclkid: merged.msclkid || null,
  };
};

/** Brochure / optional fields — source and medium only. */
export const resolveOptionalUtmParams = (): Pick<TrackingUtmParams, 'utmSource' | 'utmMedium'> => {
  const params = resolveTrackingUtmParams();
  return {
    utmSource: params.utmSource,
    utmMedium: params.utmMedium,
  };
};

/** Not named `source` / `medium`: GA4 treats those event params as the session's traffic source. */
export const getCampaignEventData = (): Record<string, string> => {
  const params = resolveTrackingUtmParams();
  const eventData: Record<string, string> = {};

  if (params.utmSource) {
    eventData.first_touch_source = params.utmSource;
  }
  if (params.utmMedium) {
    eventData.first_touch_medium = params.utmMedium;
  }
  if (params.utmCampaign) {
    eventData.campaign = params.utmCampaign;
  }
  if (params.utmId) {
    eventData.campaign_id = params.utmId;
  }
  if (params.gclid) {
    eventData.gclid = params.gclid;
  }
  if (params.msclkid) {
    eventData.msclkid = params.msclkid;
  }

  return eventData;
};

/** Append stored/live campaign params to a URL (e.g. Book Now → checkout). */
export const appendCampaignParamsToUrl = (url: string): string => {
  if (!url) {
    return url;
  }

  const fromUrl = paramsFromSearch(window.location.search);
  const stored = readStore();
  const merged: CampaignParams = { ...fromUrl, ...stored };

  if (Object.keys(merged).length === 0) {
    return url;
  }

  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const queryIndex = withoutHash.indexOf('?');
  const base = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const existingQuery = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '';

  const parts: string[] = [];
  if (existingQuery) {
    parts.push(existingQuery);
  }

  for (const key of CAMPAIGN_KEYS) {
    const value = merged[key];
    if (!value) {
      continue;
    }
    // Avoid duplicating if already present (including non-standard your-holiday&… queries).
    if (existingQuery.includes(`${key}=`)) {
      continue;
    }
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }

  if (parts.length === 0) {
    return url;
  }

  return `${base}?${parts.join('&')}${hash}`;
};

/** Append missing first-touch campaign params to a checkout query string. */
export const ensureCampaignParamsOnCheckoutQuery = (queryString: string): string => {
  const stored = readStore();
  const fromQuery = paramsFromSearch(`?${queryString}`);
  const merged: CampaignParams = { ...fromQuery, ...stored };

  let result = queryString;
  for (const key of CAMPAIGN_KEYS) {
    const value = merged[key];
    if (!value || result.includes(`${key}=`)) {
      continue;
    }
    result += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  }
  return result;
};

/** Call once on app boot so landing UTMs are stored before navigation. */
export const initUtmPersistence = (): void => {
  captureCampaignParamsFromUrl();
};
